import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { wasteProviderInfoService } from '../../../../services/wasteProviderInfo';
import { Alert, ActivityIndicator } from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('WasteProviderInput');

export const WasteProviderInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { wasteProviderId?: string; isEdit?: boolean }>, string>>();
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  const [showAutopayMethodModal, setShowAutopayMethodModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const wasteProviderId = (route.params as any)?.wasteProviderId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Helper: format currency display
  const formatCurrency = (text: string) => {
    const cleaned = text.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Helper: format date as dd/mm/yyyy while typing
  const formatDate = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');
    
    // Format as dd/mm/yyyy
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  const serviceTypes = [
    'Regular Waste',
    'Recycling',
    'Yard Waste',
    'Bulk Waste',
    'Hazardous Waste',
    'Compost',
    'All Services',
  ];

  const autopayMethods = [
    'Credit Card',
    'Debit Card',
    'Bank Transfer',
    'Check',
    'Direct Debit',
  ];

  const [formData, setFormData] = useState({
    companyName: '',
    serviceType: '',
    accountNumber: '',
    serviceAddress: '',
    billingAddress: '',
    customerServicePhone: '',
    emergencyPhone: '',
    website: '',
    username: '',
    password: '',
    pickupSchedule: '',
    monthlyCost: '',
    autopayEnabled: false,
    autopayMethod: '',
    contractEndDate: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [savedWasteProviderId, setSavedWasteProviderId] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && wasteProviderId) {
      (async () => {
        try {
          // Load existing waste provider record
          const list = await wasteProviderInfoService.getAllWasteProviderInfo();
          const record = list.find((w: any) => w.id === wasteProviderId);
          if (record) {
            setFormData(prev => ({
              ...prev,
              companyName: record.companyName || '',
              serviceType: record.serviceType || '',
              accountNumber: record.accountNumber || '',
              serviceAddress: record.serviceAddress || '',
              billingAddress: record.billingAddress || '',
              customerServicePhone: formatPhoneNumber(record.customerServicePhone || ''),
              emergencyPhone: formatPhoneNumber(record.emergencyPhone || ''),
              website: record.website || '',
              pickupSchedule: record.pickupSchedule || '',
              monthlyCost: record.monthlyCost ? formatCurrency(record.monthlyCost.toString()) : '',
              autopayEnabled: record.autopayEnabled || false,
              autopayMethod: record.autopayMethod || '',
              contractEndDate: record.contractEndDate || '',
              notes: record.notes || '',
            }));
            // Try to load portal credentials
            try {
              const creds = await vaultService.getPortalCredentials(wasteProviderId, 'waste-provider');
              if (creds && (creds.username || creds.password || creds.accountNumber)) {
                if (creds.username || creds.password) setHasOnlinePortal(true);
                setFormData(prev => ({
                  ...prev,
                  username: creds.username || '',
                  password: creds.password || '',
                  accountNumber: creds.accountNumber || prev.accountNumber,
                }));
              }
            } catch {}
          }
        } catch (e) {
          logger.debug('Failed to load waste provider for edit', e);
        }
      })();
    }
  }, [isEdit, wasteProviderId]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      const payload: any = {
        companyName: formData.companyName || undefined,
        serviceType: formData.serviceType || undefined,
        // K5: the account number is a login; it goes only to the encrypted
        // portal record below (the server never kept it in a column).
        serviceAddress: formData.serviceAddress || undefined,
        billingAddress: formData.billingAddress || undefined,
        customerServicePhone: formData.customerServicePhone.replace(/\D/g, ''),
        emergencyPhone: formData.emergencyPhone.replace(/\D/g, ''),
        website: formData.website || undefined,
        pickupSchedule: formData.pickupSchedule || undefined,
        monthlyCost: formData.monthlyCost ? parseFloat(formatCurrency(formData.monthlyCost)) : undefined,
        autopayEnabled: formData.autopayEnabled,
        autopayMethod: formData.autopayMethod || undefined,
        contractEndDate: formData.contractEndDate || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update waste provider record
      let savedId = wasteProviderId as string | undefined;
      if (isEdit && wasteProviderId) {
        const updated = await wasteProviderInfoService.updateWasteProviderInfo(wasteProviderId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id || wasteProviderId;
      } else {
        const saved = await wasteProviderInfoService.createWasteProviderInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedWasteProviderId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.accountNumber)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for waste provider ID:', savedId);
          await vaultService.storePortalCredentials(savedId, 'waste-provider', {
            username: formData.username,
            password: formData.password,
            accountNumber: formData.accountNumber,
          });
          logger.debug('[Portal Debug] Successfully stored portal credentials in vault');
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for waste provider portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Waste provider information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('WasteProviderLockUnlock', { wasteProviderId: wasteProviderId })
          }
        ]);
      } else {
        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
      }
    } catch (error: any) {
      logger.error('Error saving waste provider info:', error);
      Alert.alert('Error', 'Failed to save waste provider information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      companyName: '',
      serviceType: '',
      accountNumber: '',
      serviceAddress: '',
      billingAddress: '',
      customerServicePhone: '',
      emergencyPhone: '',
      website: '',
      username: '',
      password: '',
      pickupSchedule: '',
      monthlyCost: '',
      autopayEnabled: false,
      autopayMethod: '',
      contractEndDate: '',
      notes: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    const idToUse = savedWasteProviderId || wasteProviderId;
    logger.debug('[Done Debug] Using ID:', idToUse, 'savedWasteProviderId:', savedWasteProviderId, 'wasteProviderId:', wasteProviderId);
    navigation.navigate('WasteProviderLockUnlock', { wasteProviderId: idToUse });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit Waste Provider Info' : 'Add Waste Provider Info'}</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Waste Provider Company Name</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Enter Waste Provider Company Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Type</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowServiceTypeModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.serviceType && { color: '#A9A9A9' }
              ]}>
                {formData.serviceType || 'Select Service Type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Customer Service Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.customerServicePhone}
              onChangeText={(text) => setFormData({ ...formData, customerServicePhone: formatPhoneNumber(text) })}
              placeholder="Enter Customer Service Phone"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyPhone}
              onChangeText={(text) => setFormData({ ...formData, emergencyPhone: formatPhoneNumber(text) })}
              placeholder="Enter Emergency Phone"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Number 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Enter Account Number"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showAccountNumber}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowAccountNumber(!showAccountNumber)}
              >
                <Image
                  source={showAccountNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Address</Text>
            <TextInput
              style={styles.input}
              value={formData.serviceAddress}
              onChangeText={(text) => setFormData({ ...formData, serviceAddress: text })}
              placeholder="Enter Service Address"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Billing Address</Text>
            <TextInput
              style={styles.input}
              value={formData.billingAddress}
              onChangeText={(text) => setFormData({ ...formData, billingAddress: text })}
              placeholder="Enter Billing Address"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Enter Website"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your waste provider have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pickup Schedule</Text>
            <TextInput
              style={styles.input}
              value={formData.pickupSchedule}
              onChangeText={(text) => setFormData({ ...formData, pickupSchedule: text })}
              placeholder="Enter Pickup Schedule"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly Cost</Text>
            <TextInput
              style={styles.input}
              value={formData.monthlyCost}
              onChangeText={(text) => setFormData({ ...formData, monthlyCost: formatCurrency(text) })}
              placeholder="Enter Monthly Cost"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setFormData({ ...formData, autopayEnabled: !formData.autopayEnabled })}
            >
              <View style={[styles.toggle, formData.autopayEnabled && styles.toggleActive]}>
                <View style={[styles.toggleThumb, formData.autopayEnabled && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Autopay Enabled</Text>
            </TouchableOpacity>
          </View>

          {formData.autopayEnabled && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Autopay Method</Text>
              <TouchableOpacity
                style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                onPress={() => setShowAutopayMethodModal(true)}
              >
                <Text style={[
                  { fontSize: 16, color: '#1C2C4F' },
                  !formData.autopayMethod && { color: '#A9A9A9' }
                ]}>
                  {formData.autopayMethod || 'Select Autopay Method'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
                />
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract End Date</Text>
            <TextInput
              style={styles.input}
              value={formData.contractEndDate}
              onChangeText={(text) => setFormData({ ...formData, contractEndDate: formatDate(text) })}
              placeholder="dd/mm/yyyy"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any additional notes"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>


          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={[styles.submitButton, styles.addAnotherButton]}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.submitButton, styles.doneButton]}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <ActivityIndicator size="large" color="#29BFC0" />
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />

      {/* Service Type Modal */}
      <Modal
        visible={showServiceTypeModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <ScrollView>
              {serviceTypes.map((serviceType) => (
                <TouchableOpacity
                  key={serviceType}
                  style={styles.stateItem}
                  onPress={() => {
                    setFormData({ ...formData, serviceType });
                    setShowServiceTypeModal(false);
                  }}
                >
                  <Text style={styles.stateText}>{serviceType}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowServiceTypeModal(false)}
            >
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showAutopayMethodModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <ScrollView>
              {autopayMethods.map((method) => (
                <TouchableOpacity
                  key={method}
                  style={styles.stateItem}
                  onPress={() => {
                    setFormData({ ...formData, autopayMethod: method });
                    setShowAutopayMethodModal(false);
                  }}
                >
                  <Text style={styles.stateText}>{method}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowAutopayMethodModal(false)}
            >
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
      position: 'absolute',
      width: '85%',
      height: '85%',
      opacity: 0.1,
    },
  logo: {
    width: s(125),
    height: s(125),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: ms(10),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    padding: ms(20),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
  },
  closeButton: {
    padding: ms(5),
  },
  closeButtonText: {
    fontSize: ms(24, 0.3),
    color: '#666',
  },
  stateItem: {
    paddingVertical: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E7EB',
  },
  stateText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});
